import { Link } from 'react-router-dom';
import { BADGE_LABELS, type ProductEntry } from '@/content/products';
import { Badge } from './Badge';
import { Icon } from './Icon';

/**
 * 产品卡：图标 + 名称 + 行内徽标 + 一行描述 + 带箭头的动作链接。
 *
 * 动作刻意做成文字链接而不是按钮 —— 一屏可能出现二三十张卡，
 * 每张都放实心按钮会把整页的层次压平。这与阿里云产品页的处理一致。
 */
export function ProductCard({
  product,
  showNote = false,
  withAnchor = false,
  className,
}: {
  product: ProductEntry;
  showNote?: boolean;
  /** 只在该产品的归属类目里挂 id，避免「精选产品」重复渲染时出现重复锚点。 */
  withAnchor?: boolean;
  className?: string;
}) {
  const isExternal = (to: string) => /^https?:/i.test(to);

  return (
    <article
      id={withAnchor ? product.id : undefined}
      className={['card-surface card-hover flex h-full flex-col gap-3 p-5', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-grad-tint text-brand">
          <Icon name={product.icon} size={18} />
        </span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 pt-1">
          <h3 className="text-[1.0625rem] font-semibold text-fg">{product.name}</h3>
          {product.badge ? (
            <Badge tone={product.badge === 'soon' ? 'tint' : 'solid'}>{BADGE_LABELS[product.badge]}</Badge>
          ) : null}
        </div>
      </div>

      <p className="text-sm leading-relaxed text-fg-muted">{product.tagline}</p>
      {showNote && product.note ? <p className="text-sm leading-relaxed text-fg-subtle">{product.note}</p> : null}

      <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-2 pt-1">
        {product.actions.map((action) =>
          isExternal(action.to) ? (
            <a
              key={`${action.label}-${action.to}`}
              href={action.to}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm font-medium text-brand transition-colors hover:text-brand-hover"
            >
              {action.label}
              <Icon name="external" size={13} />
            </a>
          ) : (
            <Link
              key={`${action.label}-${action.to}`}
              to={action.to}
              className="inline-flex items-center gap-1 text-sm font-medium text-brand transition-colors hover:text-brand-hover"
            >
              {action.label}
              <Icon name="arrowRight" size={13} />
            </Link>
          ),
        )}
      </div>
    </article>
  );
}
